// GuardLists: the word lists the output guard enforces, as DATA (banned
// words, catalog words, the published price figures, number phrases Otto may
// say about Otto's Office). Versioned with the conduct rules that forbid them
// (ConductRules.guard_lists), so Otto Studio shows, diffs and rolls them back
// like every other behavior document (moved here from @otto/guard at
// integration, Sept 28 2026). Patterns that recognize a claim (phones,
// compliance, notes) stay code in @otto/guard: they enforce a rule, they are
// not Otto's words.
import { z } from "zod";

const DASHES = /[–—]/;

const Lang = z.enum(["en", "es"]);

export const GuardTerm = z.object({
  /** Matched as a whole word or phrase, ignoring case and accents; hyphens and spaces are the same. */
  term: z
    .string()
    .min(1)
    .max(80)
    .refine((s) => !DASHES.test(s), { message: "No em or en dashes" }),
  lang: Lang,
  /** When true, Otto may use the word after the owner used it first (their agents, their plans, their catalog). */
  echo_ok: z.boolean().default(true),
  /** "review" flags for a person to look at; "hard" follows the conduct rule's hard gate. */
  severity: z.enum(["hard", "review"]).default("hard"),
  note: z.string().max(500).optional(),
});
export type GuardTerm = z.infer<typeof GuardTerm>;

export const AllowedNumberPhrase = z.object({
  phrase: z.string().min(1).max(120),
  lang: Lang,
  note: z.string().max(500).optional(),
});
export type AllowedNumberPhrase = z.infer<typeof AllowedNumberPhrase>;

function validRegExp(source: string): boolean {
  try {
    new RegExp(source, "g");
    return true;
  } catch {
    // An invalid pattern is reported as a validation issue by the refine below.
    return false;
  }
}

export const GuardLists = z
  .object({
    list_format: z.literal("otto-guard-lists@1"),
    draft_label: z.string().max(200).optional(),
    banned_terms: z.array(GuardTerm).max(500),
    catalog_terms: z.array(GuardTerm).max(500),
    /** Regex sources for catalog ids (case-sensitive). No workflow names, ever. */
    catalog_id_patterns: z.array(z.string().min(1).max(200).refine(validRegExp, { message: "Not a valid regular expression" })).max(20).default([]),
    /** The only price figures Otto may say, and only when answering a price question (the Sept 14 rule). */
    published_price_figures: z.array(z.number().positive()).max(10),
    /** Phrases with numbers that come from the facts block or the brief, such as "six months from now". */
    allowed_number_phrases: z.array(AllowedNumberPhrase).max(200).default([]),
  })
  .superRefine((lists, ctx) => {
    const seen = new Set<string>();
    for (const [where, arr] of [["banned_terms", lists.banned_terms], ["catalog_terms", lists.catalog_terms]] as const) {
      arr.forEach((t, i) => {
        const key = `${where}:${t.term.toLowerCase()}`;
        if (seen.has(key)) ctx.addIssue({ code: "custom", path: [where, i, "term"], message: `Duplicate term: ${t.term}` });
        seen.add(key);
      });
    }
  });
export type GuardLists = z.infer<typeof GuardLists>;
export type GuardListsInput = z.input<typeof GuardLists>;
